import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsCategoryOpen(false);
  }, [location]);

  const isHomePage = location.pathname === '/';

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.REACT_APP_NAVIGATE(`/category/all?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHomePage
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-book-read-line text-white text-lg"></i>
            </div>
            <span className={`font-heading text-xl whitespace-nowrap ${
              isScrolled || !isHomePage ? 'text-foreground-950' : 'text-white'
            }`}>
              虫虫漫画
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/'
                  ? isScrolled ? 'text-primary-500 bg-primary-50' : 'text-white bg-white/20'
                  : isScrolled ? 'text-foreground-700 hover:text-primary-500 hover:bg-primary-50' : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              首页
            </Link>
            <div className="relative">
              <button
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  location.pathname.startsWith('/category')
                    ? isScrolled ? 'text-primary-500 bg-primary-50' : 'text-white bg-white/20'
                    : isScrolled ? 'text-foreground-700 hover:text-primary-500 hover:bg-primary-50' : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                分类
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isCategoryOpen && (
                <div className="absolute top-full left-0 mt-1 w-44 rounded-lg bg-background-50 border border-background-200/70 shadow-lg py-1 z-50">
                  {categories.filter((c) => c.slug !== 'all').map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-500 transition-colors"
                      onClick={() => setIsCategoryOpen(false)}
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/about'
                  ? isScrolled ? 'text-primary-500 bg-primary-50' : 'text-white bg-white/20'
                  : isScrolled ? 'text-foreground-700 hover:text-primary-500 hover:bg-primary-50' : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              关于我们
            </Link>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索漫画..."
                className={`w-48 lg:w-56 pl-9 pr-3 py-2 rounded-lg text-sm border transition-all focus:outline-none focus:w-64 ${
                  isScrolled || !isHomePage
                    ? 'bg-background-100 border-background-200 text-foreground-950 placeholder-foreground-400 focus:border-primary-300'
                    : 'bg-white/15 border-white/20 text-white placeholder-white/50 focus:border-white/40 focus:bg-white/20'
                }`}
              />
              <button type="submit" className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                isScrolled || !isHomePage ? 'text-foreground-400' : 'text-white/60'
              }`}>
                <i className="ri-search-line text-sm"></i>
              </button>
            </form>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <i className={`text-xl ${isScrolled || !isHomePage ? 'text-foreground-950' : 'text-white'} ${
              isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
            }`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 shadow-lg">
          <div className="px-4 py-3">
            <form onSubmit={handleSearch} className="relative mb-3">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索漫画..."
                className="w-full pl-9 pr-3 py-2.5 rounded-lg text-sm border border-background-200 bg-background-100 text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-300"
              />
              <button type="submit" className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">
                <i className="ri-search-line text-sm"></i>
              </button>
            </form>

            <Link
              to="/"
              className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${
                location.pathname === '/' ? 'text-primary-500 bg-primary-50' : 'text-foreground-700'
              }`}
            >
              <i className="ri-home-4-line text-lg"></i> 首页
            </Link>

            <div className="mt-1">
              <div className="px-3 py-2 text-xs text-foreground-400 font-medium uppercase">漫画分类</div>
              {categories.filter((c) => c.slug !== 'all').map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${
                    location.pathname === `/category/${cat.slug}`
                      ? 'text-primary-500 bg-primary-50'
                      : 'text-foreground-600'
                  }`}
                >
                  <i className={`${cat.icon} text-lg`}></i> {cat.name}
                </Link>
              ))}
            </div>

            <Link
              to="/about"
              className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium mt-1 ${
                location.pathname === '/about' ? 'text-primary-500 bg-primary-50' : 'text-foreground-700'
              }`}
            >
              <i className="ri-information-line text-lg"></i> 关于我们
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}